<template>
  <div class="smart-image-container" :style="containerStyle">
    <!-- 骨架屏占位符 -->
    <div 
      v-if="loading" 
      class="image-skeleton"
      :style="skeletonStyle"
    >
      <div class="skeleton-animation"></div>
    </div>
    
    <!-- 实际图片 -->
    <img
      v-show="!loading && !error"
      :src="currentSrc"
      :alt="alt"
      :class="['smart-image', imageClass]"
      :style="imageStyle"
      @load="onLoad"
      @error="onError"
      :loading="lazyLoad ? 'lazy' : 'eager'"
    />
    
    <!-- 错误占位符 -->
    <div 
      v-if="error" 
      class="image-error"
      :style="errorStyle"
    >
      <div class="error-icon">📷</div>
      <div class="error-text">图片加载失败</div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SmartImage',
  props: {
    // 图片源路径（不带扩展名）
    src: {
      type: String,
      required: true
    },
    // 替代文本
    alt: {
      type: String,
      default: ''
    },
    // 容器宽度
    width: {
      type: [String, Number],
      default: 'auto'
    },
    // 容器高度
    height: {
      type: [String, Number],
      default: 'auto'
    },
    // 图片适应方式
    objectFit: {
      type: String,
      default: 'cover',
      validator: (value) => ['cover', 'contain', 'fill', 'scale-down', 'none'].includes(value)
    },
    // 是否启用懒加载
    lazyLoad: {
      type: Boolean,
      default: true
    },
    // 额外的CSS类
    imageClass: {
      type: String,
      default: ''
    },
    // 是否显示骨架屏
    showSkeleton: {
      type: Boolean,
      default: true
    },
    // 图片质量优先级：webp > png > jpg
    preferWebp: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      loading: true,
      error: false,
      currentSrc: '',
      supportsWebp: false
    }
  },
  computed: {
    containerStyle() {
      return {
        width: this.normalizeSize(this.width),
        height: this.normalizeSize(this.height),
        position: 'relative',
        overflow: 'hidden'
      }
    },
    imageStyle() {
      return {
        width: '100%',
        height: '100%',
        objectFit: this.objectFit,
        transition: 'opacity 0.3s ease'
      }
    },
    skeletonStyle() {
      return {
        width: '100%',
        height: '100%',
        backgroundColor: '#f5f5f5',
        position: 'relative',
        overflow: 'hidden'
      }
    },
    errorStyle() {
      return {
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f9f9f9',
        color: '#999'
      }
    }
  },
  async mounted() {
    // 检测WebP支持
    this.supportsWebp = await this.checkWebpSupport()
    this.loadOptimalImage()
  },
  watch: {
    src() {
      this.loading = true
      this.error = false
      this.loadOptimalImage()
    }
  },
  methods: {
    normalizeSize(size) {
      if (typeof size === 'number') {
        return `${size}px`
      }
      return size
    },
    
    // 检测浏览器是否支持WebP
    checkWebpSupport() {
      return new Promise((resolve) => {
        const webp = new Image()
        webp.onload = webp.onerror = () => {
          resolve(webp.height === 2)
        }
        webp.src = 'data:image/webp;base64,UklGRjoAAABXRUJQVlA4IC4AAACyAgCdASoCAAIALmk0mk0iIiIiIgBoSygABc6WWgAA/veff/0PP8bA//LwYAAA'
      })
    },
    
    // 加载最优图片格式
    async loadOptimalImage() {
      const srcPath = this.src.startsWith('@/') ? this.src : `@/${this.src}`
      
      // 尝试加载顺序：WebP -> PNG -> JPG
      const formats = this.preferWebp && this.supportsWebp 
        ? ['webp', 'png', 'jpg', 'jpeg']
        : ['png', 'jpg', 'jpeg', 'webp']
      
      for (const format of formats) {
        try {
          const imageSrc = require(`${srcPath.replace(/\.[^.]+$/, '')}.${format}`)
          this.currentSrc = imageSrc
          return
        } catch (e) {
          // 继续尝试下一个格式
        }
      }
      
      // 如果所有格式都失败，尝试原始路径
      try {
        this.currentSrc = require(srcPath)
      } catch (e) {
        this.onError()
      }
    },
    
    onLoad() {
      this.loading = false
      this.error = false
      this.$emit('load')
    },
    
    onError() {
      this.loading = false
      this.error = true
      this.$emit('error')
    }
  }
}
</script>

<style lang="scss" scoped>
.smart-image-container {
  display: inline-block;
}

.smart-image {
  display: block;
}

.image-skeleton {
  .skeleton-animation {
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, 
      transparent 0%, 
      rgba(255, 255, 255, 0.4) 50%, 
      transparent 100%
    );
    animation: skeleton-loading 1.5s infinite;
  }
}

@keyframes skeleton-loading {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

.image-error {
  .error-icon {
    font-size: 2rem;
    margin-bottom: 0.5rem;
  }
  
  .error-text {
    font-size: 0.875rem;
  }
}

// 响应式图片
.smart-image {
  max-width: 100%;
  height: auto;
}

// 移动端优化
@media (max-width: 768px) {
  .image-skeleton {
    .skeleton-animation {
      animation-duration: 1s; // 移动端加快动画速度
    }
  }
}
</style>
